class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.trendingLoaded = false;
    this.trendingHTML = '';
    this.RECENT_KEY = 'raw_ps_recent';
    this.MAX_RECENT = 5;
    this.setupEventListeners();
    this.setupResultClickTracking();
    this.setupModalCloseListener();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  /* ── Recent searches (localStorage) ── */
  getRecent() {
    try {
      return JSON.parse(localStorage.getItem(this.RECENT_KEY) || '[]');
    } catch { return []; }
  }

  saveRecent(term) {
    if (!term || term.length < 2) return;
    try {
      let recent = this.getRecent().filter(t => t.toLowerCase() !== term.toLowerCase());
      recent.unshift(term);
      recent = recent.slice(0, this.MAX_RECENT);
      localStorage.setItem(this.RECENT_KEY, JSON.stringify(recent));
    } catch {}
  }

  removeRecent(term) {
    try {
      const recent = this.getRecent().filter(t => t !== term);
      localStorage.setItem(this.RECENT_KEY, JSON.stringify(recent));
    } catch {}
  }

  renderRecentSearches(container) {
    const recent = this.getRecent();

    /* Remove any existing recent section first */
    const existing = container.querySelector('#raw-ps-recent');
    if (existing) existing.remove();

    if (recent.length === 0) return;

    /* Build element */
    const section = document.createElement('div');
    section.id = 'raw-ps-recent';
    section.className = 'raw-ps__recent-section';

    const rows = recent.map(term => {
      const escaped = term.replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;');
      return '<div class="raw-ps__recent-row" data-term="' + escaped + '">'
        + '<span class="raw-ps__recent-icon"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></span>'
        + '<span class="raw-ps__recent-text">' + escaped + '</span>'
        + '<button class="raw-ps__recent-remove" data-remove="' + escaped + '" aria-label="Remove">✕</button>'
        + '</div>';
    }).join('');

    section.innerHTML = '<p class="raw-ps__section-label">Recent searches</p><div id="raw-ps-recent-list">' + rows + '</div>';

    /* Prepend before everything else */
    container.prepend(section);

    /* Events */
    section.querySelectorAll('.raw-ps__recent-row').forEach(row => {
      row.addEventListener('click', (e) => {
        if (e.target.closest('.raw-ps__recent-remove')) return;
        this.input.value = row.dataset.term;
        this.searchTerm = row.dataset.term;
        this.getSearchResults(row.dataset.term);
      });
    });

    section.querySelectorAll('.raw-ps__recent-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeRecent(btn.dataset.remove);
        this.renderRecentSearches(container);
      });
    });
  }

  /* ── Empty state ── */
  showEmptyState() {
    if (this.trendingLoaded && this.trendingHTML) {
      this.predictiveSearchResults.innerHTML = this.trendingHTML;
      this.removeAttribute('loading');
      this.setAttribute('results', true);
      this.open();
      requestAnimationFrame(() => this.renderRecentSearches(this.predictiveSearchResults));
      return;
    }

    /* Render recent searches immediately before skeleton */
    this.renderRecentSearches(this.predictiveSearchResults);

    /* Skeleton while loading */
    this.predictiveSearchResults.innerHTML = `
      <div class="raw-ps__empty-state">
        <div class="raw-ps__section" style="border-top:none">
          <p class="raw-ps__section-label">Trending products</p>
          <div class="raw-ps__skeleton-grid">
            ${Array(4).fill(`
              <div class="raw-ps__skeleton-card">
                <div class="raw-ps__skeleton-img"></div>
                <div class="raw-ps__skeleton-lines">
                  <div class="raw-ps__skeleton-line raw-ps__skeleton-line--title"></div>
                  <div class="raw-ps__skeleton-line raw-ps__skeleton-line--price"></div>
                </div>
              </div>`).join('')}
          </div>
        </div>
      </div>`;
    this.removeAttribute('loading');
    this.setAttribute('results', true);
    this.open();

    fetch('/search?section_id=predictive-search-trending&q=__empty__')
      .then(res => res.text())
      .then(text => {
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const section = parsed.querySelector('#shopify-section-predictive-search-trending');
        const html = section ? section.innerHTML : '';
        if (html) {
          this.trendingHTML = html;
          this.trendingLoaded = true;
          if (!this.getQuery().length) {
            this.predictiveSearchResults.innerHTML = html;
            requestAnimationFrame(() => this.renderRecentSearches(this.predictiveSearchResults));
          }
        }
      })
      .catch(() => {});
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.showEmptyState();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    if (query.length) this.saveRecent(query);
    if (!query.length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) {
      this.showEmptyState();
      return;
    }
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.showEmptyState();
      return;
    }
    event.preventDefault();
    switch (event.code) {
      case 'ArrowUp':   this.switchOption('up');   break;
      case 'ArrowDown': this.switchOption('down'); break;
      case 'Enter':
        if (this.getQuery().length) this.saveRecent(this.getQuery());
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) return;
      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll('li, button.raw-ps__search-for-btn')
    ).filter(el => el.offsetParent !== null);

    let activeElementIndex = 0;
    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;
    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) selectedElementIndex = i;
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;
    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  setupModalCloseListener() {
    /* Listen for the details element closing — most reliable approach */
    const detailsModal = this.closest('details');
    if (detailsModal) {
      detailsModal.addEventListener('toggle', () => {
        if (!detailsModal.open) {
          this.close(true);
          /* Force hide on mobile */
          if (window.innerWidth <= 749) {
            this.predictiveSearchResults.style.cssText = 'display: none !important;';
          }
        }
      });
    }

    /* Also catch X button click by class */
    document.addEventListener('click', (e) => {
      const closeBtn = e.target.closest('.search-modal__close-button');
      if (closeBtn) {
        this.close(true);
        if (window.innerWidth <= 749) {
          this.predictiveSearchResults.style.cssText = 'display: none !important;';
        }
      }
    });
  }

  setupResultClickTracking() {
    /* Save search term whenever user clicks any result link or suggestion */
    this.predictiveSearchResults.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (!link) return;

      /* If clicking a suggestion chip, save the suggestion text */
      const chipText = e.target.closest('.raw-ps__suggestion-chip');
      if (chipText) {
        const text = chipText.querySelector('span')?.textContent?.trim();
        if (text && text.length >= 2) this.saveRecent(text);
        return;
      }

      /* Otherwise save the current query */
      const term = this.getQuery();
      if (term.length >= 2) this.saveRecent(term);
    });
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=product,collection,query,page&resources[options][fields]=title,product_type,variants.title,tag`,
      { signal: this.abortController.signal }
    )
      .then(response => {
        if (!response.ok) { this.close(); throw new Error(response.status); }
        return response.text();
      })
      .then(text => {
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const sectionEl = parsed.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = sectionEl ? sectionEl.innerHTML : parsed.body.innerHTML;

        this.allPredictiveSearchInstances.forEach(instance => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });

        if (this.getQuery() === searchTerm) {
          this.renderSearchResults(resultsMarkup);
        }
      })
      .catch(error => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => { this.statusElement.setAttribute('aria-hidden', 'true'); }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || ''
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    if (window.innerWidth <= 749) {
      this.predictiveSearchResults.style.display = 'block';
      this.positionDropdownMobile();
    }
    /* Always re-render recent searches when dropdown opens (handles mobile cache case) */
    if (!this.getQuery().length) {
      requestAnimationFrame(() => this.renderRecentSearches(this.predictiveSearchResults));
    }
  }

  positionDropdownMobile() {
    if (window.innerWidth > 749) return;

    /* Find the search modal content wrapper — position below it */
    const searchContent = document.querySelector('.search-modal__content');
    if (searchContent) {
      const rect = searchContent.getBoundingClientRect();
      const top = Math.max(rect.bottom, 0);
      this.predictiveSearchResults.style.top = top + 'px';
      this.predictiveSearchResults.style.maxHeight = (window.innerHeight - top - 16) + 'px';
      return;
    }

    /* Fallback: use search input bottom */
    const input = this.querySelector('input');
    if (!input) return;
    const rect = input.getBoundingClientRect();
    const top = rect.bottom + 4;
    this.predictiveSearchResults.style.top = top + 'px';
    this.predictiveSearchResults.style.maxHeight = (window.innerHeight - top - 16) + 'px';
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    /* On mobile use display:none to fully hide the fixed dropdown */
    if (window.innerWidth <= 749) {
      this.predictiveSearchResults.style.cssText = 'display: none !important;';
    } else {
      this.predictiveSearchResults.removeAttribute('style');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);